import { z } from "zod";
import { MEMBER_DUE_STATUS } from "./collections.js";

/* ===========================================================================
 * US-E10.F1-0075 — Debts / Offset / Loan / Pay-on-behalf (@UC0061/0062/0054)
 * Phase 7 — @gate money,ledger 🔴.
 *
 * B1: mọi số tiền = STRING minor unit (BigInt-as-string, KHÔNG float).
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/* ---------------------------------------------------------------------------
 * Pay-on-behalf (US-1 · @UC0061/0062)
 * ------------------------------------------------------------------------- */

export const PAY_ON_BEHALF_STATUS = {
  PROPOSED: "proposed",
  ACCEPTED: "accepted",
  DECLINED: "declined",
} as const;

export type PayOnBehalfStatus =
  (typeof PAY_ON_BEHALF_STATUS)[keyof typeof PAY_ON_BEHALF_STATUS];

/** POST /v1/member-dues/:id/pay-on-behalf — A đề xuất nộp hộ due của B (AC1). */
export const proposePayOnBehalfResponseSchema = z
  .object({
    payOnBehalfId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    payerMemberId: z.string().uuid(),
    beneficiaryMemberId: z.string().uuid(),
    status: z.literal(PAY_ON_BEHALF_STATUS.PROPOSED),
    proposedAt: z.string().datetime(),
  })
  .strict();

export type ProposePayOnBehalfResponse = z.infer<
  typeof proposePayOnBehalfResponseSchema
>;

export const PAY_ON_BEHALF_DECISION = {
  ACCEPT: "accept",
  DECLINE: "decline",
} as const;

export const respondPayOnBehalfRequestSchema = z
  .object({
    decision: z.enum([PAY_ON_BEHALF_DECISION.ACCEPT, PAY_ON_BEHALF_DECISION.DECLINE]),
  })
  .strict();

export type RespondPayOnBehalfRequest = z.infer<
  typeof respondPayOnBehalfRequestSchema
>;

/** POST /v1/pay-on-behalf/:id/respond — B accept/decline (AC2/AC3). */
export const respondPayOnBehalfResponseSchema = z
  .object({
    payOnBehalfId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    status: z.enum([PAY_ON_BEHALF_STATUS.ACCEPTED, PAY_ON_BEHALF_STATUS.DECLINED]),
    /** Null khi decline (không ledger); có khi accept. */
    ledgerTxId: z.string().uuid().nullable(),
    newDueStatus: z.enum([
      MEMBER_DUE_STATUS.PAID_ON_BEHALF,
      MEMBER_DUE_STATUS.PENDING,
    ]),
    respondedAt: z.string().datetime(),
  })
  .strict();

export type RespondPayOnBehalfResponse = z.infer<
  typeof respondPayOnBehalfResponseSchema
>;

/* ---------------------------------------------------------------------------
 * Offset / cấn trừ (US-2 · @UC0054)
 * ------------------------------------------------------------------------- */

/** POST /v1/member-dues/:id/offset — cấn advance_credit ↔ due unpaid (treasurer). */
export const offsetResponseSchema = z
  .object({
    memberDueId: z.string().uuid(),
    offsetAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    remainingDueMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    fullyOffset: z.boolean(),
    /** OFFSET ledger tx (nội bộ MEMBER_PAYABLE↔MEMBER_RECEIVABLE) — luôn có. */
    ledgerTxId: z.string().uuid(),
    newDueStatus: z.enum([MEMBER_DUE_STATUS.PAID, MEMBER_DUE_STATUS.PENDING]),
    offsetAt: z.string().datetime(),
  })
  .strict();

export type OffsetResponse = z.infer<typeof offsetResponseSchema>;

/* ---------------------------------------------------------------------------
 * Loan / mượn tạm (US-3 · @UC0054)
 * ------------------------------------------------------------------------- */

export const LOAN_STATUS = {
  OPEN: "open",
  PARTIALLY_REPAID: "partially_repaid",
  REPAID: "repaid",
} as const;

export type LoanStatus = (typeof LOAN_STATUS)[keyof typeof LOAN_STATUS];

export const createLoanRequestSchema = z
  .object({
    fundId: z.string().uuid(),
    borrower: z.string().min(1).max(200),
    principalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
  })
  .strict();

export type CreateLoanRequest = z.infer<typeof createLoanRequestSchema>;

export const loanResponseSchema = z
  .object({
    loanId: z.string().uuid(),
    fundId: z.string().uuid(),
    borrower: z.string(),
    principalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    status: z.enum([LOAN_STATUS.OPEN, LOAN_STATUS.PARTIALLY_REPAID, LOAN_STATUS.REPAID]),
    ledgerTxId: z.string().uuid(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type LoanResponse = z.infer<typeof loanResponseSchema>;

export const loanRepaymentRequestSchema = z
  .object({
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type LoanRepaymentRequest = z.infer<typeof loanRepaymentRequestSchema>;

export const loanRepaymentResponseSchema = z
  .object({
    loanId: z.string().uuid(),
    repaymentMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    status: z.enum([LOAN_STATUS.OPEN, LOAN_STATUS.PARTIALLY_REPAID, LOAN_STATUS.REPAID]),
    ledgerTxId: z.string().uuid(),
    repaidAt: z.string().datetime(),
  })
  .strict();

export type LoanRepaymentResponse = z.infer<typeof loanRepaymentResponseSchema>;

/* ---------------------------------------------------------------------------
 * Settlement collection (US-5 · @UC0053)
 * POST /v1/funds/:id/collections kind=settlement — tất toán cuối kỳ.
 * ------------------------------------------------------------------------- */

/** Optional title (không gửi → service dùng mặc định). Không nhận amount (tính từ vị thế ledger). */
export const createSettlementRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(120).optional(),
  })
  .strict();

export type CreateSettlementRequest = z.infer<typeof createSettlementRequestSchema>;

export const settlementResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    fundId: z.string().uuid(),
    kind: z.literal("settlement"),
    title: z.string(),
    currency: z.string().length(3),
    /** Số member_dues sinh (chỉ member còn nợ; member dư/không nợ KHÔNG sinh — AC4). */
    duesCreated: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type SettlementResponse = z.infer<typeof settlementResponseSchema>;

/* ---------------------------------------------------------------------------
 * Peer-debt settlement (US-E15.F1-0176 · @UC9211/9212/9213)
 * Trả nợ member↔member (on_behalf/expense_split). claim (debtor) → respond (creditor).
 * Tiền = STRING minor-unit (BigInt ở service). .strict() từ chối field lạ.
 * ------------------------------------------------------------------------- */

export const PEER_SETTLEMENT_STATUS = {
  CLAIMED: "claimed",
  SETTLED: "settled",
  REJECTED: "rejected",
  /**
   * US-E15.F5-0180 (@UC9223) — trạng thái TRUNG THỰC THỨ BA: "đã chuyển – chưa đối chiếu". Con nợ
   * đã báo chuyển (claimed) và (peer nhỏ) hết X giờ → worker chuyển sang đây — KHÔNG phải "đã nhận"
   * (settled). Máy KHÔNG mạo danh lời người (P2.2): trạng thái này KHÔNG đóng nợ, KHÔNG post ledger,
   * KHÔNG đổi số dư quỹ thật. Creditor vẫn bấm "đã nhận" (→ settled) hoặc "chưa nhận" (→ pending/claimed).
   */
  TRANSFERRED_UNRECONCILED: "transferred_unreconciled",
} as const;

export type PeerSettlementStatus =
  (typeof PEER_SETTLEMENT_STATUS)[keyof typeof PEER_SETTLEMENT_STATUS];

export const PEER_SETTLEMENT_DECISION = {
  ACCEPT: "accept",
  DECLINE: "decline",
} as const;

export type PeerSettlementDecision =
  (typeof PEER_SETTLEMENT_DECISION)[keyof typeof PEER_SETTLEMENT_DECISION];

/** POST /v1/funds/:fundId/debts/settle — debtor CLAIM "đã chuyển" (AC1). Lump: refDebtEntryIds ≥1. */
export const claimPeerSettlementSchema = z
  .object({
    debtorMemberId: z.string().uuid().optional(),
    creditorMemberId: z.string().uuid(),
    refDebtEntryIds: z.array(z.string().uuid()).min(1),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type ClaimPeerSettlementRequest = z.infer<typeof claimPeerSettlementSchema>;

/** POST /v1/funds/:fundId/debts/settlements/:id/respond — creditor accept/decline. */
export const respondPeerSettlementSchema = z
  .object({
    decision: z.enum([
      PEER_SETTLEMENT_DECISION.ACCEPT,
      PEER_SETTLEMENT_DECISION.DECLINE,
    ]),
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();

export type RespondPeerSettlementRequest = z.infer<
  typeof respondPeerSettlementSchema
>;

export const peerSettlementResponseSchema = z
  .object({
    settlementId: z.string().uuid(),
    status: z.enum([
      PEER_SETTLEMENT_STATUS.CLAIMED,
      PEER_SETTLEMENT_STATUS.SETTLED,
      PEER_SETTLEMENT_STATUS.REJECTED,
      PEER_SETTLEMENT_STATUS.TRANSFERRED_UNRECONCILED,
    ]),
    /**
     * US-E15.F5-0180 (@UC9225) — nhánh xác nhận theo nguồn/ngưỡng (chỉ có ở claim peer). Client hiển
     * thị "chờ 72h" / "chờ chủ nợ" / "bắt tay". Optional (respond không cần).
     */
    mode: z
      .enum([
        "manual_only",
        "auto_after_72h",
        "hold_until_creditor",
        "require_handshake",
      ])
      .optional(),
    /** Số đã trả (bút toán settle vừa post; 0 khi claim/reject). */
    settledMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Nợ ròng CÒN LẠI debtor→creditor sau bước này (≥0). */
    remainingNetMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    /** Null khi claim/reject (chưa/không post); có khi accept post SETTLEMENT. */
    ledgerTxId: z.string().uuid().nullable(),
    /** Null khi mới claim (chưa creditor phản hồi). */
    respondedAt: z.string().datetime().nullable(),
  })
  .strict();

export type PeerSettlementResponse = z.infer<typeof peerSettlementResponseSchema>;

/* ---------------------------------------------------------------------------
 * Financial position guard codes (US-4)
 * ------------------------------------------------------------------------- */

export const FINANCIAL_POSITION_CODE = {
  NONZERO: "FINANCIAL_POSITION_NONZERO",
} as const;

export const FINANCIAL_POSITION_NONZERO_MESSAGE =
  "Không thể thực hiện: thành viên còn vị thế tài chính chưa tất toán (khoản nợ/mượn/phải đóng).";

export const OWNER_HAS_FUNDS_MESSAGE =
  "Không thể xoá tài khoản: bạn còn là chủ nhóm có quỹ đang hoạt động hoặc còn nợ chưa tất toán.";

/** DELETE /v1/me — soft-delete + anonymize (AC3). */
export const deleteMeResponseSchema = z
  .object({
    status: z.literal("anonymized"),
    deletedAt: z.string().datetime(),
  })
  .strict();

export type DeleteMeResponse = z.infer<typeof deleteMeResponseSchema>;

/* ---------------------------------------------------------------------------
 * Advance-credit / ứng trước — quỹ nợ member (US-E15.F3-0178 · @UC9217/9218/9219)
 * PRODUCER duy nhất còn thiếu để OffsetService chạy (offset.service.ts:199 chờ
 * DebtEntry kind='advance_credit'). Member nộp dư / treasurer cấp credit → quỹ ghi
 * nợ với member. Tiền = STRING minor-unit (BigInt ở service). .strict() từ chối field lạ.
 * ------------------------------------------------------------------------- */

/** POST /v1/funds/:fundId/advance-credit — treasurer ghi member ứng trước (quỹ nợ member). */
export const grantAdvanceCreditSchema = z
  .object({
    /** groupMember.id của member được ghi credit (validate ở service: ACTIVE cùng group fund). */
    memberId: z.string().uuid(),
    /** Số tiền ứng (minor-unit, string số nguyên > 0). "0"/"1.5" → reject. */
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).refine((v) => BigInt(v) > 0n, {
      message: "amountMinor phải > 0",
    }),
    /** Bắt buộc — scope idempotency (không double-post cùng key). */
    idempotencyKey: z.string().trim().min(1).max(200),
    reason: z.string().trim().max(500).optional(),
  })
  .strict();

export type GrantAdvanceCreditRequest = z.infer<typeof grantAdvanceCreditSchema>;

export const advanceCreditResponseSchema = z
  .object({
    debtEntryId: z.string().uuid(),
    /** ADVANCE_CREDIT ledger tx (DEBIT CASH / CREDIT MEMBER_PAYABLE) — luôn có. */
    ledgerTxId: z.string().uuid(),
    memberId: z.string().uuid(),
    /** credit khả dụng SAU grant = Σ advance_credit − Σ offset (string minor-unit). */
    availableCreditMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    createdAt: z.string().datetime(),
  })
  .strict();

export type AdvanceCreditResponse = z.infer<typeof advanceCreditResponseSchema>;
